// How the review screen turns a stored game into the game the review shows:
// the moves and positions parsed by the chess API, where the opening ends and
// the endgame begins, and which side is the reader's. Pure of React and the
// DOM; every dependency is an argument, so it can be tested without the app.
import type { Chess } from '@chess/api-chess';
import { clocksOf, initialClockOf, readerSideOf, sidesOf, endgameStartOf, openingEndOf, type PhasesProvider, type ReviewGame } from '@chess/api-review';
import type { Fen, GameRecord, Move, ReviewPhases } from '@chess/api-model';

/** Decides where the opening ends and the endgame begins for a game's moves. */
export type PhasesOf = (moves: Move[], positions: Fen[]) => Promise<ReviewPhases>;

/** How long the `phases` provider may take to appear before the rules without a book stand in. */
export const PHASES_PROVIDER_WAIT_MS = 3000;

/**
 * Where the opening ends and the endgame begins, decided once per game so
 * that every tab, the puzzles and the history agree. The `phases` provider
 * (plugin-review-phases) decides it with the opening book; a build without
 * one, or one that is slow, falls back to the rules without a book.
 */
export function createPhasesOf(deps: {
  /** The `phases` provider, asked for again for each game. */
  provider: () => Promise<PhasesProvider | undefined>;
  /** Told when the provider fails, before the fallback is used. */
  warn: (message: string, data: { error: string }) => void;
  waitMs?: number;
}): PhasesOf {
  const waitMs = deps.waitMs ?? PHASES_PROVIDER_WAIT_MS;
  return async (moves, positions) => {
    try {
      const provider = await Promise.race([
        deps.provider(),
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), waitMs)),
      ]);
      if (provider) return await provider.phasesOf({ moves, positions });
    } catch (error) {
      deps.warn('phases provider failed; using the rules without a book', { error: String(error) });
    }
    return fallbackPhases(moves, positions);
  };
}

/** The phases by the rules alone, without an opening book. */
export function fallbackPhases(moves: Move[], positions: Fen[]): ReviewPhases {
  return { openingEnd: openingEndOf(moves.length, undefined), endgameStart: endgameStartOf(positions) };
}

/**
 * The review's game for a stored record, or undefined when there is none.
 * With `[%clk]` comments in the PGN the game carries each move's clock and
 * the initial time from the time control, for the board's player lines.
 */
export async function reviewGameOf(
  id: string,
  record: Pick<GameRecord, 'pgn' | 'owner' | 'white' | 'black' | 'timeControl'> | undefined,
  chess: Pick<Chess, 'parsePgn'>,
  phasesOf: PhasesOf,
): Promise<ReviewGame | undefined> {
  if (!record) return undefined;
  const parsed = chess.parsePgn(record.pgn);
  const phases = await phasesOf(parsed.moves, parsed.positions);
  // The reader at the bottom, and every score read for the reader.
  const sides = sidesOf(readerSideOf(record));
  const game: ReviewGame = { id, moves: parsed.moves, positions: parsed.positions, result: parsed.result, phases, sides };
  const clocks = clocksOf(record.pgn);
  if (clocks.some((c) => c !== undefined)) {
    game.clocks = clocks;
    const initialClock = initialClockOf(record.timeControl);
    if (initialClock !== undefined) game.initialClock = initialClock;
  }
  return game;
}
